import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { useUnsavedChangesSource } from '../appUpdate';
import { tokens } from '../../theme/tokens';
import { InlineMessage, LoadingState, NumberField, SaveBar, SectionCard, StatusChip, parseNumberInput } from '../ui';
import { TrialFeatureTitle } from '../trial/TrialFeatureBadge';

/** The conditions a rule may price; `new` is the reference and has no rule. */
export type PricedCondition = 'open_box' | 'demo' | 'display';
export const PRICED_CONDITIONS: PricedCondition[] = ['open_box', 'demo', 'display'];

export interface ConditionPricingRule {
  percentOff?: number;
  fixedOff?: number;
  floor?: number;
  allowBelowMap?: boolean;
}
export type ConditionPricingRuleMap = Partial<Record<PricedCondition, ConditionPricingRule>>;

interface RulesResponse {
  rules?: ConditionPricingRuleMap;
  planLocked?: boolean;
}

type Mode = 'none' | 'percent' | 'fixed';

interface RuleDraft {
  mode: Mode;
  amount: string;
  floor: string;
  allowBelowMap: boolean;
}

type Draft = Record<PricedCondition, RuleDraft>;

const emptyRule = (): RuleDraft => ({ mode: 'none', amount: '', floor: '', allowBelowMap: false });

const toDraft = (rules: ConditionPricingRuleMap | undefined): Draft => {
  const out = {} as Draft;
  for (const c of PRICED_CONDITIONS) {
    const r = rules?.[c];
    out[c] = r
      ? {
          mode: r.percentOff ? 'percent' : r.fixedOff ? 'fixed' : 'none',
          amount: r.percentOff != null ? String(r.percentOff) : r.fixedOff != null ? String(r.fixedOff) : '',
          floor: r.floor != null ? String(r.floor) : '',
          allowBelowMap: r.allowBelowMap === true,
        }
      : emptyRule();
  }
  return out;
};

const fromDraft = (draft: Draft): Record<PricedCondition, ConditionPricingRule | null> => {
  const out = {} as Record<PricedCondition, ConditionPricingRule | null>;
  for (const c of PRICED_CONDITIONS) {
    const d = draft[c];
    const amount = parseNumberInput(d.amount);
    if (d.mode === 'none' || amount == null || amount <= 0) {
      out[c] = null;
      continue;
    }
    const floor = parseNumberInput(d.floor);
    out[c] = {
      ...(d.mode === 'percent' ? { percentOff: amount } : { fixedOff: amount }),
      ...(floor != null && floor > 0 ? { floor } : {}),
      ...(d.allowBelowMap ? { allowBelowMap: true } : {}),
    };
  }
  return out;
};

const sameDraft = (a: Draft, b: Draft): boolean => JSON.stringify(fromDraft(a)) === JSON.stringify(fromDraft(b));

/** A worked example on a $1,000 new price, so the rule's effect is visible as it is edited. */
const EXAMPLE_NEW = 1000;
const exampleFor = (rule: ConditionPricingRule | null): number | null => {
  if (!rule) return null;
  let price = EXAMPLE_NEW;
  if (rule.percentOff) price = EXAMPLE_NEW * (1 - Math.min(rule.percentOff, 100) / 100);
  else if (rule.fixedOff) price = EXAMPLE_NEW - rule.fixedOff;
  if (rule.floor != null && price < rule.floor) price = Math.min(rule.floor, EXAMPLE_NEW);
  return price > 0 ? Math.round(price * 100) / 100 : null;
};

/**
 * Per-condition pricing (flag `condition_pricing`, commercial home `customPricing`):
 * one rule per condition, applied to the resolved new price when a unit of that
 * condition is listed. Edits are local until Save (one call); the bar shows only
 * while something is unsaved and registers with the app-update prompt.
 */
export const ConditionPricingCard: React.FC = () => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery<RulesResponse>({
    queryKey: ['conditionPricing'],
    queryFn: async () => {
      const response = await makeApiCall('/store/condition-pricing', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error((await response.json().catch(() => ({})))?.error || t('store.conditionPricing.loadError'));
      return response.json();
    },
  });
  const planLocked = data?.planLocked === true;

  const saved = useMemo(() => toDraft(data?.rules), [data?.rules]);
  const [draft, setDraft] = useState<Draft>(saved);
  useEffect(() => setDraft(saved), [saved]);
  const dirty = !sameDraft(draft, saved);

  const invalid = PRICED_CONDITIONS.some((c) => {
    const d = draft[c];
    if (d.mode === 'none') return false;
    const amount = parseNumberInput(d.amount);
    if (amount == null || amount <= 0) return true;
    if (d.mode === 'percent' && amount > 90) return true;
    const floor = parseNumberInput(d.floor);
    return d.floor !== '' && (floor == null || floor < 0);
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const response = await makeApiCall('/store/condition-pricing', {
        method: 'PUT',
        headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: fromDraft(draft) }),
      });
      if (!response.ok) throw new Error((await response.json().catch(() => ({})))?.error || t('store.conditionPricing.saveError'));
      return response.json() as Promise<RulesResponse>;
    },
    onSuccess: (body) => {
      queryClient.setQueryData(['conditionPricing'], body);
      showToast(t('store.conditionPricing.saved'), 'success');
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  useUnsavedChangesSource({
    dirty,
    save: async () => {
      await saveMutation.mutateAsync();
    },
    discard: () => setDraft(saved),
  });

  const setRule = (c: PricedCondition, patch: Partial<RuleDraft>) => setDraft((prev) => ({ ...prev, [c]: { ...prev[c], ...patch } }));

  const ruleCount = PRICED_CONDITIONS.filter((c) => fromDraft(saved)[c]).length;

  if (isLoading) return <LoadingState />;

  return (
    <>
      <SectionCard
        title={<TrialFeatureTitle title={t('store.conditionPricing.title')} feature="customPricing" />}
        subtitle={t('store.conditionPricing.description')}
        actions={
          planLocked ? (
            <StatusChip tone="caution" label={t('store.conditionPricing.planLocked')} dot />
          ) : (
            <StatusChip tone={ruleCount > 0 ? 'positive' : 'neutral'} label={t('store.conditionPricing.coverage', { count: ruleCount, total: PRICED_CONDITIONS.length })} dot />
          )
        }
      >
        {error && (
          <InlineMessage severity="error">
            {(error as Error).message}{' '}
            <Button size="small" onClick={() => refetch()}>{t('store.conditionPricing.retry')}</Button>
          </InlineMessage>
        )}
        {planLocked && (
          <InlineMessage severity="info">
            {t('store.conditionPricing.planLockedHelp')} <RouterLink to="/app/billing">{t('store.conditionPricing.planLockedLink')}</RouterLink>
          </InlineMessage>
        )}
        <Typography sx={{ fontSize: 13, color: tokens.ink.muted, mt: planLocked ? 1.5 : 0, mb: 1.5 }}>
          {t('store.conditionPricing.order')}
        </Typography>
        <Stack spacing={2}>
          {PRICED_CONDITIONS.map((c) => {
            const d = draft[c];
            const rule = fromDraft(draft)[c];
            const example = exampleFor(rule);
            return (
              <Box
                key={c}
                sx={{
                  border: '1px solid',
                  borderColor: tokens.line.default,
                  borderRadius: '12px',
                  p: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t(`store.skuCrosswalk.condition.${c}`)}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
                    {rule
                      ? example != null
                        ? t('store.conditionPricing.example', { price: example.toFixed(2), newPrice: EXAMPLE_NEW.toFixed(2) })
                        : t('store.conditionPricing.exampleNone')
                      : t('store.conditionPricing.noRule')}
                  </Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  label={t('store.conditionPricing.fields.mode')}
                  value={d.mode}
                  onChange={(e) => setRule(c, { mode: e.target.value as Mode, amount: e.target.value === 'none' ? '' : d.amount })}
                  disabled={planLocked}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="none">{t('store.conditionPricing.mode.none')}</MenuItem>
                  <MenuItem value="percent">{t('store.conditionPricing.mode.percent')}</MenuItem>
                  <MenuItem value="fixed">{t('store.conditionPricing.mode.fixed')}</MenuItem>
                </TextField>
                {d.mode !== 'none' && (
                  <>
                    <NumberField
                      size="small"
                      label={t('store.conditionPricing.fields.amount')}
                      value={d.amount}
                      onChange={(v) => setRule(c, { amount: v })}
                      min={0}
                      max={d.mode === 'percent' ? 90 : undefined}
                      adornment={d.mode === 'percent' ? '%' : '$'}
                      allowEmpty={false}
                      disabled={planLocked}
                      sx={{ width: 140 }}
                    />
                    <NumberField
                      size="small"
                      label={t('store.conditionPricing.fields.floor')}
                      value={d.floor}
                      onChange={(v) => setRule(c, { floor: v })}
                      min={0}
                      adornment="$"
                      helperText={t('store.conditionPricing.fields.floorHelp')}
                      disabled={planLocked}
                      sx={{ width: 160 }}
                    />
                    <FormControlLabel
                      sx={{ flex: '1 1 260px', alignItems: 'flex-start', m: 0 }}
                      control={<Checkbox size="small" checked={d.allowBelowMap} onChange={(e) => setRule(c, { allowBelowMap: e.target.checked })} disabled={planLocked} sx={{ mt: -0.5 }} />}
                      label={
                        <Box>
                          <Typography sx={{ fontSize: 13.5 }}>{t('store.conditionPricing.fields.allowBelowMap')}</Typography>
                          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{t('store.conditionPricing.fields.allowBelowMapHelp')}</Typography>
                        </Box>
                      }
                    />
                  </>
                )}
              </Box>
            );
          })}
        </Stack>
        <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 1.5 }}>{t('store.conditionPricing.footnote')}</Typography>
      </SectionCard>
      <SaveBar
        dirty={dirty && !planLocked}
        message={invalid ? t('store.conditionPricing.invalid') : t('store.conditionPricing.saveMessage')}
        saving={saveMutation.isPending}
        onSave={() => {
          if (!invalid) saveMutation.mutate();
        }}
        onDiscard={() => setDraft(saved)}
      />
    </>
  );
};

export default ConditionPricingCard;
